import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext, ThemeContext } from '../Context';
import { 
  CardContent, 
  Typography, 
  Box,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import {
  Description,
  History,
  BarChart,
  CheckCircleOutline,
  WarningAmber,
  AllInclusive
} from '@mui/icons-material';

// Convertir les jours en texte lisible
const formatRetention = (days) => {
  if (days >= 365) {
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;
    const months = Math.floor(remainingDays / 30);
    if (months > 0) return `${years} an${years > 1 ? 's' : ''} et ${months} mois`;
    return `${years} an${years > 1 ? 's' : ''}`;
  }
  if (days >= 30) {
    const months = Math.floor(days / 30);
    return `${months} mois`;
  }
  return `${days} jour${days > 1 ? 's' : ''}`;
};


const DataCategory = ({ icon: Icon, title, subtitle, autoDelete, retentionDays, isDark }) => {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: isDark 
          ? '1px solid rgba(144, 202, 249, 0.12)' 
          : '1px solid rgba(0, 0, 0, 0.08)',
        bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
          borderColor: isDark ? 'rgba(144, 202, 249, 0.3)' : 'rgba(25, 118, 210, 0.3)',
        }
      }}
    >
      {/* En-tête de la catégorie */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 42,
            height: 42,
            borderRadius: '12px',
            bgcolor: isDark ? 'rgba(144, 202, 249, 0.1)' : 'rgba(25, 118, 210, 0.08)',
          }}
        >
          <Icon sx={{ color: isDark ? '#90caf9' : '#1976d2', fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600, 
              color: isDark ? '#fff' : 'inherit',
              lineHeight: 1.3
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ color: isDark ? '#78909c' : 'text.secondary' }}
          >
            {subtitle}
          </Typography>
        </Box>
        <Chip 
          icon={autoDelete 
            ? <WarningAmber sx={{ fontSize: 16 }} /> 
            : <AllInclusive sx={{ fontSize: 16 }} />
          }
          label={autoDelete ? 'Suppression auto' : 'Conservation illimitée'}
          size="small"
          sx={{
            bgcolor: autoDelete 
              ? (isDark ? 'rgba(255, 167, 38, 0.15)' : 'rgba(255, 152, 0, 0.1)')
              : (isDark ? 'rgba(102, 187, 106, 0.15)' : 'rgba(46, 125, 50, 0.1)'),
            color: autoDelete 
              ? (isDark ? '#ffa726' : '#e65100')
              : (isDark ? '#66bb6a' : '#2e7d32'),
            border: autoDelete
              ? (isDark ? '1px solid rgba(255, 167, 38, 0.3)' : '1px solid rgba(255, 152, 0, 0.3)')
              : (isDark ? '1px solid rgba(102, 187, 106, 0.3)' : '1px solid rgba(46, 125, 50, 0.3)'),
            fontWeight: 500,
            '& .MuiChip-icon': {
              color: 'inherit'
            }
          }}
        />
      </Box>

      {/* Détail de la rétention */}
      {autoDelete ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography 
            variant="body2" 
            sx={{ color: isDark ? '#b0bec5' : 'text.secondary' }}
          >
            Suppression automatique après{' '}
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 700, color: isDark ? '#fff' : 'inherit' }}
            >
              {retentionDays} jours
            </Typography>
            {' '}({formatRetention(retentionDays)})
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 1 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: isDark ? 'rgba(102, 187, 106, 0.08)' : 'rgba(46, 125, 50, 0.05)',
            }}
          >
            <CheckCircleOutline sx={{ color: isDark ? '#66bb6a' : '#2e7d32', fontSize: 20 }} />
            <Typography 
              variant="body2" 
              sx={{ color: isDark ? '#a5d6a7' : '#2e7d32' }}
            >
              Vos données sont conservées sans limite de temps
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const AccountDataInfo = () => {
  const { user } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');

  const isDark = theme === 'dark';

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/dataretention');
        setSettings(response.data);
      } catch (err) {
        setError('Erreur lors de la récupération des paramètres de rétention des données.');
        console.error(err);
      }
    };

    fetchSettings();
  }, []);

  if (!user) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Alert severity="warning">Veuillez vous connecter pour voir les informations de rétention.</Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!settings) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Box display="flex" justifyContent="center" m={2}>
          <CircularProgress sx={{ color: isDark ? '#90caf9' : 'inherit' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component={Paper}
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: isDark ? '#233044' : '#ffffff',
        color: isDark ? '#fff' : 'inherit',
        boxShadow: isDark 
          ? '0 4px 20px rgba(0, 0, 0, 0.5)'
          : '0 4px 20px rgba(0,0,0,0.05)',
      }}
    >
      <CardContent>
        <Typography 
          variant="h5" 
          component="h2" 
          gutterBottom
          sx={{ color: isDark ? '#90caf9' : 'inherit' }}
        >
          Conservation de mes données
        </Typography>

        <Typography 
          variant="body2" 
          sx={{ 
            color: isDark ? '#b0bec5' : 'text.secondary', 
            mb: 3 
          }}
        >
          Voici les durées de conservation configurées par votre administrateur pour chaque type de données vous concernant.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DataCategory 
            icon={Description}
            title="Procès-verbaux"
            subtitle="Documents rédigés et leurs contenus"
            autoDelete={settings.reports?.autoDelete}
            retentionDays={settings.reports?.retentionDays}
            isDark={isDark}
          />

          <DataCategory 
            icon={History}
            title="Journaux (logs)"
            subtitle="Historique de vos connexions et actions"
            autoDelete={settings.logs?.autoDelete}
            retentionDays={settings.logs?.retentionDays}
            isDark={isDark}
          />

          <DataCategory 
            icon={BarChart}
            title="Statistiques"
            subtitle="Compteurs anonymes d'utilisation"
            autoDelete={settings.statistics?.autoDelete}
            retentionDays={settings.statistics?.retentionDays}
            isDark={isDark}
          />
        </Box>

        <Divider sx={{ my: 3, borderColor: isDark ? 'rgba(144, 202, 249, 0.12)' : 'inherit' }} />

        <Box 
          sx={{ 
            p: 2, 
            borderRadius: 2,
            bgcolor: isDark ? 'rgba(144, 202, 249, 0.06)' : 'rgba(25, 118, 210, 0.04)',
            border: isDark 
              ? '1px solid rgba(144, 202, 249, 0.12)' 
              : '1px solid rgba(25, 118, 210, 0.1)',
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: isDark ? '#78909c' : 'text.secondary',
              lineHeight: 1.6
            }}
          >
            Ces paramètres sont définis par l'administrateur de votre organisation. 
            Si vous avez des questions concernant la politique de conservation des données, 
            veuillez contacter votre responsable.
          </Typography>
        </Box>
      </CardContent>
    </Box>
  );
};

export default AccountDataInfo;
